import React from 'react';
import { Check, Copy } from 'lucide-react';
import { SettingsGroup } from '../integrations/integrationUi';
import { publicApiInstanceBaseUrl } from '../../../../config/publicApi';

/** Einstieg in die externe API mit den echten Werten dieser Instanz. Details stehen in der Referenz darunter. */
export function ApiQuickstart({ instanceId }: { instanceId: string | null }) {
  const base = publicApiInstanceBaseUrl(instanceId);

  const createExample = `curl -X POST "${base}/projects" \\
  -H "Authorization: Bearer $MEISTERPFAD_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: besichtigung-4711" \\
  -d '{
    "category": "Heizung",
    "lead_title": "Wärmepumpe Beratung",
    "first_name": "Max",
    "last_name": "Muster",
    "email": "max@example.com",
    "phone": "+49 123 456789",
    "address": "Musterstraße 1, 12345 Musterstadt",
    "notes": "Altbau, Gasheizung von 2004"
  }'`;

  const readOffersExample = `curl "${base}/offers?updated_since=2026-10-01T00:00:00Z&limit=50" \\
  -H "Authorization: Bearer $MEISTERPFAD_API_KEY"

curl "${base}/offers/<ANGEBOTS-ID>" \\
  -H "Authorization: Bearer $MEISTERPFAD_API_KEY"`;

  const uploadExample = `curl -X POST "${base}/projects/<PROJEKT-ID>/documents" \\
  -H "Authorization: Bearer $MEISTERPFAD_API_KEY" \\
  -F "file=@besichtigung.jpg"`;

  return (
    <>
      <SettingsGroup
        title="So funktioniert's"
        description="Für Website-Formulare, Baustellen-Apps, Portale oder Automatisierungen wie Lovable, Make oder Zapier."
      >
        <ol className="space-y-3 px-5 py-4 text-sm text-app-text">
          <Step n={1}>Oben einen Schlüssel mit passenden Berechtigungen erstellen und im Secret-Speicher eures Servers ablegen.</Step>
          <Step n={2}>
            Jede Anfrage trägt <code className="text-xs">Authorization: Bearer &lt;Schlüssel&gt;</code>. Die Basis-URL enthält eure Instanz-ID.
          </Step>
          <Step n={3}>Projekte anlegen und lesen, Angebote mit Positionen lesen oder anlegen, Fotos und Dokumente hochladen.</Step>
        </ol>
        <p className="px-5 py-4 text-sm text-amber-900 bg-amber-50/70">
          Den Schlüssel nur auf dem Server verwenden, nie im Browser oder in öffentlichem Code. Wer ihn hat, kann im Rahmen seiner Berechtigungen auf eure Daten zugreifen.
        </p>
      </SettingsGroup>

      <SettingsGroup title="Basis-URL" description="Alle Endpunkte hängen an dieser Adresse.">
        <div className="space-y-3 px-5 py-4">
          <Endpoint method="BASE" path={base} />
          <p className="text-xs text-app-text-muted">
            Der Header <code>Idempotency-Key</code> ist optional: Wiederholt eure App eine Anfrage nach einem Timeout mit demselben Key, entsteht kein Duplikat.
          </p>
        </div>
      </SettingsGroup>

      <SettingsGroup title="Projekt anlegen" description="Legt Projekt und Kontakt an und benachrichtigt das Büro.">
        <div className="space-y-4 px-5 py-4">
          <Endpoint method="POST" path={`${base}/projects`} />
          <CodeBlock code={createExample} />
          <p className="text-xs text-app-text-muted">
            Ohne <code>contact_id</code> braucht es <code>last_name</code> oder <code>name</code>. Alle Felder stehen in der Referenz unter „Projekt anlegen“.
          </p>
        </div>
      </SettingsGroup>

      <SettingsGroup title="Angebote lesen" description="Positionen mit Nummer, Menge, Einheit, Kurz-/Langtext, EP/GP und Status wie im PDF.">
        <div className="space-y-4 px-5 py-4">
          <Endpoint method="GET" path={`${base}/offers`} />
          <CodeBlock code={readOffersExample} />
          <p className="text-xs text-app-text-muted">
            <code>updated_since</code> erfasst neue Angebote und Änderungen am Vorgang. Positions- und Statusänderungen erkennt ihr über die Angebotsliste eines Projekts und <code>content_updated_at</code> in der Einzelansicht.
          </p>
        </div>
      </SettingsGroup>

      <SettingsGroup title="Dokument hochladen" description="Fotos, Aufmaße, PDFs. Eine Datei pro Anfrage im Feld file, bis 50 MB.">
        <div className="space-y-4 px-5 py-4">
          <Endpoint method="POST" path={`${base}/projects/<PROJEKT-ID>/documents`} />
          <CodeBlock code={uploadExample} />
        </div>
      </SettingsGroup>
    </>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-app-surface-muted text-xs font-semibold text-app-text-muted">{n}</span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}

export function MethodBadge({ method }: { method: string }) {
  const tone =
    method === 'GET'
      ? 'bg-sky-100 text-sky-800'
      : method === 'POST'
        ? 'bg-emerald-100 text-emerald-800'
        : method === 'PATCH'
          ? 'bg-amber-100 text-amber-800'
          : method === 'DELETE'
            ? 'bg-red-100 text-red-800'
            : 'bg-app-surface text-app-text-muted';
  return <span className={`inline-block w-14 shrink-0 rounded px-1.5 py-0.5 text-center text-[11px] font-bold ${tone}`}>{method}</span>;
}

export function Endpoint({ method, path }: { method: string; path: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg border border-app-border bg-app-surface-muted px-3 py-2">
      <MethodBadge method={method} />
      <code className="min-w-0 break-all font-mono text-xs text-app-text">{path}</code>
    </div>
  );
}

export function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Zwischenablage nicht verfügbar: Code bleibt markierbar.
    }
  };
  return (
    <div className="relative">
      <pre className="app-scrollbar overflow-x-auto rounded-lg bg-slate-900 p-4 pr-12 font-mono text-xs leading-relaxed text-slate-100">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label="Code kopieren"
        className="absolute right-2 top-2 rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
      >
        {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}
